import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, ChevronDown, LogOut, Search, Settings, User } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { post } from '@/lib/api';
import { initials } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';

export function Topbar({ title }: { title: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const session = useAuthStore((s) => s.session);
  const activeOutletId = useAuthStore((s) => s.activeOutletId);
  const setActiveOutlet = useAuthStore((s) => s.setActiveOutlet);
  const clear = useAuthStore((s) => s.clear);

  const outlets = session?.outlets ?? [];
  const activeOutlet = outlets.find((o) => o.id === activeOutletId);

  const logout = useMutation({
    mutationFn: () => post('/auth/logout'),
    // Clear locally regardless of the response. If the token is already gone
    // server-side the call 401s, and refusing to sign out because sign-out
    // failed is the worst possible outcome.
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-surface px-6">
      <h1 className="shrink-0 text-[17px] font-semibold tracking-tight">{title}</h1>

      <GlobalSearch />

      <div className="ml-auto flex items-center gap-2">
        {/* Only shown when there is genuinely a choice to make. */}
        {outlets.length > 1 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm ring-1 ring-border-strong transition-colors hover:bg-sunken"
              aria-label="Switch outlet"
            >
              <Building2 className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
              <div className="text-left leading-tight">
                <span className="block text-[10px] uppercase tracking-wide text-ink-muted">
                  Outlet
                </span>
                <span className="block font-medium">{activeOutlet?.name ?? 'Select'}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="floating z-50 min-w-56 rounded-lg border border-border bg-surface p-1"
              >
                {outlets.map((outlet) => (
                  <DropdownMenu.Item
                    key={outlet.id}
                    onSelect={() => {
                      setActiveOutlet(outlet.id);
                      // Every cached list is outlet-scoped — stock, sales,
                      // registers. Keeping them would show the previous
                      // shop's numbers under the new shop's name.
                      queryClient.clear();
                    }}
                    className={cn(
                      'flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm outline-none',
                      outlet.id === activeOutletId
                        ? 'bg-brand-subtle text-brand'
                        : 'hover:bg-sunken',
                    )}
                  >
                    <span>{outlet.name}</span>
                    <span className="text-xs text-ink-muted">{outlet.code}</span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}

        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex items-center gap-2.5 rounded-lg py-1.5 pl-1.5 pr-2 transition-colors hover:bg-sunken">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-subtle text-xs font-semibold text-brand">
              {initials(session?.user.name ?? '')}
            </span>
            <div className="hidden text-left leading-tight sm:block">
              <span className="block text-sm font-medium">{session?.user.name}</span>
              <span className="block text-[11px] text-ink-muted">
                {session?.user.is_owner ? 'Owner' : (session?.user.role?.name ?? 'Staff')}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 text-ink-muted" />
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="floating z-50 min-w-56 rounded-lg border border-border bg-surface p-1"
            >
              <div className="px-3 py-2">
                <p className="truncate text-sm font-medium">{session?.user.name}</p>
                <p className="truncate text-xs text-ink-muted">{session?.user.email}</p>
              </div>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                onSelect={() => navigate('/settings/profile')}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none hover:bg-sunken"
              >
                <User className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
                My profile
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={() => navigate('/settings/security')}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none hover:bg-sunken"
              >
                <Settings className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
                Security &amp; devices
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                onSelect={() => logout.mutate()}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-critical outline-none hover:bg-critical-subtle"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.75} />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}

function GlobalSearch() {
  const [value, setValue] = useState('');

  return (
    <div className="relative hidden max-w-md flex-1 md:block">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
        strokeWidth={1.75}
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search products, sales, customers…"
        aria-label="Search"
        className="h-10 w-full rounded-lg bg-sunken pl-9 pr-14 text-sm outline-none ring-1 ring-transparent transition-shadow focus:bg-surface focus:ring-brand"
      />
      <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-ink-muted lg:block">
        ⌘K
      </kbd>
    </div>
  );
}
